import { axiosSecuredInstance } from "configurations/axiosConfig";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { DownloadFileType } from "utils/data/enum";
import { dateTimeAsFilename, downloadByBase64, getBase64DataType } from "utils/shared";
import { showToast } from "controls/global/stewart-toast";

// One entry of the PolicyErrors collection returned inline on each row.
// Mirrors Stewart.Shared.Entities.PolicyData.PolicyError (only the fields the
// UI reads are declared).
export type PolicyErrorDto = {
  PolicyErrorID: number | null;
  PolicyErrorExceptionMessage: string | null;
  ErrorFieldName: string | null;
  PolicyErrorExceptionDate: string | null;
  IdentityKey: number | null;
  IntegrationKey: string | null;
  HasGUIDChanged?: boolean | null;
  IsNew?: boolean | null;
  IsDeleted?: boolean | null;
  IsDirty?: boolean | null;
  IsReadOnly?: boolean | null;
  PartitionYear?: number | null;
  Active: number | null;
};

// Mirrors Stewart.Shared.Entities.PolicyData.PaginatedPolicyTransaction
// (PolicyTransaction + PolicyBaseEntity + paging columns). PascalCase on the
// wire because Startup.cs sets JsonSerializerOptions.PropertyNamingPolicy = null.
export type PolicyTransactionDto = {
  IdentityKey: number;

  // PolicyTransaction-specific
  PolicyTransactionID: number;
  ExternalFee: number | null;
  ExternalPremiumTax: number | null;
  ExternalRiskRate: number | null;
  ExternalRemittance: number | null;
  StageTypeCode: string | null;
  StageChangedDate: string | null;
  StateChangedByUserID: string | null;
  IsOnHold: boolean;
  OnHoldReason: string | null;
  LastHeldByUserID: string | null;
  LastHeldByUserName: string | null;
  LastOnHoldDate: string | null;
  HoldRemovedComment: string | null;
  HoldRemovedBy: string | null;
  HoldRemovedDate: string | null;
  IsForcePromoted: boolean;
  OpenErrorCount: number;
  NoteCount: number;
  LockedByUserName: string | null;
  LockedByUserID: string | null;
  LockContextActive: string | null;
  PolicyErrors?: PolicyErrorDto[] | null;
  LockedAt?: string | null;
  // Not surfaced by GetListByCriteria today (only LockedByUserName/LockContext
  // are joined) — optional so the UI degrades gracefully until the backend adds it.
  LockedDate?: string | null;

  // PolicyBaseEntity
  PolicyImportID: number | null;
  PolicyImportDetailID: number | null;
  StateCode: string | null;
  // Two-letter abbreviation for the State grid column. Not returned by the API
  // yet — cells render blank until the SP/entity add it.
  StateAbbr: string | null;
  CountyDisplayName: string | null;
  AgentID: string | null;
  FileNumber: string | null;
  ProductTypeAbbrTypeCode: string | null;
  ProductTypeAbbrTypeName: string | null;
  FormPrefix: string | null;
  PolicyNumber: string | null;
  PolicyFormName: string | null;
  SIPolicyNumber: string | null;
  OriginalPolicyNumber: string | null;
  OriginalPolicyDate: string | null;
  OriginalPolicyCoverageType: string | null;
  ReportingID: string | null;
  InLieuPolicyNumber: string | null;
  UnderwriterTypeCode: string | null;
  UnderwriterTypeName: string | null;
  PolicyYear: string | null;
  PolicyMonth: string | null;
  PolicyDay: string | null;
  PolicyMonthDay: string | null;
  EffectiveDate: string | null;
  IssueDate: string | null;
  VoidedDate: string | null;
  BatchNumber: string | null;
  BatchSeqNumber: string | null;
  ProcessYear: string | null;
  ProcessMonth: string | null;
  ProcessDate: string | null;
  AccountingPeriod: string | null;
  ReceivedDate: string | null;
  KeyedDate: string | null;
  RecordTypeCode: string | null;
  RecordTypeName: string | null;
  PropertyTypeCode: string | null;
  PropertyTypeName: string | null;
  EscrowCode: string | null;
  CountyCode: string | null;
  MunicipalityNum: string | null;
  PropertyCity: string | null;
  PropertyZipCode: string | null;
  PricingRateType: string | null;
  TransactionTypeCode: string | null;
  TransactionTypeName: string | null;
  TransCode: string | null;
  TransCode2: string | null;
  TransCode3: string | null;
  TransCode4: string | null;
  TransCode5: string | null;
  TransCode6: string | null;
  TransCode7: string | null;
  TransCode8: string | null;
  RateAmount: number | null;
  RateAmount2: number | null;
  RateAmount3: number | null;
  RateAmount4: number | null;
  RateAmount5: number | null;
  RateAmount6: number | null;
  RateAmount7: number | null;
  RateAmount8: number | null;
  LiabilityAmount: number | null;
  ReissueLiabilityAmount: number | null;
  AdditionalLiabilityAmount: number | null;
  SILiabilityAmount: number | null;
  CalculatedFee: number | null;
  ActualFee: number | null;
  CalculatedPremiumTax: number | null;
  ActualPremiumTax: number | null;
  CalculatedRemittance: number | null;
  ActualRemittance: number | null;
  OriginalRemitSplitPct: number | null;
  NewRemitSplitPct: number | null;
  CalculatedRiskRate: number | null;
  ActualRiskRate: number | null;
  DiscountAmt: number | null;
  MiscStateVolume1: number | null;
  MiscStateVolume2: number | null;
  IsOverride: boolean | null;
  OverrideAmount: number | null;
  IsSimultaneous: boolean | null;
  IsHOI: boolean | null;
  IsReIssue: boolean | null;
  IsHavePolicy: boolean | null;
  IsEdited: boolean;
  EditedByUserID: string | null;
  EditedByUser: string | null;
  LastEditedDate: string | null;
  ClosedByUserName: string | null;
  ClosedDate: string | null;
  OverrideNoteText: string | null;
  OverrideNoteBy: string | null;
  OverrideNoteDate: string | null;
  OverrideByUsername: string | null;
  OverrideDate: string | null;
  PreOverrideActualFee: number | null;
  PreOverrideActualPremiumTax: number | null;
  PreOverrideActualRiskRate: number | null;
  PreOverrideActualRemittance: number | null;
  PreOverrideCapturedDate: string | null;
  ImageNumber: string | null;
  CheckNumber: string | null;
  IsMaxLiability: string | null;
  PctAmountPolicy: number | null;
  TreatyReinsurerCode: string | null;
  // int? — non-zero = national (drives the client-side "isNational" flag).
  IsMAST: number | null;
  KeypunchID: string | null;
  ExternalRefID: string | null;
  OriginationSource: string | null;
  OriginationUser: string | null;
  ChangesetID: string | null;

  // Paging metadata (from PaginatedPolicyTransaction). TotalRows / TotalPages
  // are whole-query totals duplicated on every row by a window-function in the
  // SP — not per-page values.
  RowsPerPage: number | null;
  CurrentRow: number | null;
  TotalRows: number | null;
  CurrentPage: number | null;
  TotalPages: number | null;

  // Whole-query aggregates (same value on every row).
  TotalCount: number | null;
  TotalProducts: number | null;
  TotalOpenErrors: number | null;
  TotalSimultaneous: number | null;
  TotalLiabilityAmount: number | null;
  TotalActualFee: number | null;
  TotalActualRetention: number | null;
  TotalActualRemittance: number | null;
  TotalStates: number | null;
};

// Open-ended criteria: arbitrary PolicyBaseCriteria field names (PolicyNumber,
// AgentID, etc.) flow through via the index signature while the known fields
// stay typed.
export type PolicyTransactionCriteriaDto = {
  UserTimeZone: string;
  StageTypeCode: string;
  RequestedPage?: number;
  ListCount?: number;
  SortColumn?: string;
} & Record<string, unknown>;

export type PolicyTransactionExportCriteriaDto = {
  ExportColumns: (keyof PolicyManagementRecord)[];
  SearchCriteria: Partial<PolicyTransactionCriteriaDto> & Record<string, unknown>;
};

export type BulkHoldPolicyTransactionDto = {
  PolicyTransactionListID: string;
  OnHoldReason: string;
  IncludeSameFileTran: number; // 0 = this only, 1 = all matching
  IsOnHold: number;
}

export type SetPolicyTransactionDeletedListDto = {
  PolicyTransactionIDList: string;
  IsDeleted: boolean; // true = delete, false = restore
  Reason: string;
}

export type SetPolicyTransactionDeletedListResult = {
  success: boolean;
  errorMessage?: string;
}

export const exportPolicyTransaction = async (
  exportCriteria: PolicyTransactionExportCriteriaDto
): Promise<string | null> => {
  try {
    const getExportFilename = (): string => {
      let filename = `${exportCriteria.SearchCriteria.StageTypeCode} ${dateTimeAsFilename()}.${DownloadFileType.Xlsx}`;
      return filename;
    };
    const { data } = await axiosSecuredInstance.post(
      "/PolicyTransaction/ExportPolicyTransaction",
      exportCriteria
    );
    const filename = getExportFilename();
    downloadByBase64(`data:${getBase64DataType(DownloadFileType.Xlsx)};base64,${data}`, filename);
    return null;
  } catch (error) {
    console.error("exportPolicyTransaction failed", error);
    return null;
  }
};

export const exportPolicyHistory = async (
  exportCriteria: PolicyTransactionExportCriteriaDto
): Promise<string | null> => {
  try {
    const getExportFilename = (): string => {
      let filename = `${exportCriteria.SearchCriteria.StageTypeCode} ${dateTimeAsFilename()}.${DownloadFileType.Xlsx}`;
      return filename;
    };
    const { data } = await axiosSecuredInstance.post(
      "/PolicyHistory/ExportPolicyHistory",
      exportCriteria
    );
    const filename = getExportFilename();
    downloadByBase64(`data:${getBase64DataType(DownloadFileType.Xlsx)};base64,${data}`, filename);
    return null;
  } catch (error) {
    console.error("exportPolicyHistory failed", error);
    return null;
  }
};


// Payload for SaveStageChange — the Force Promote action. Mirrors
// PolicyTransactionStageChange (Stewart.Shared.Entities.PolicyData); the
// endpoint promotes a comma-separated list of PolicyTransactionIDs in one call.
export type PolicyTransactionStageChange = {
  PolicyTransactionListID: string; // comma-separated PolicyTransactionID values
  IsForcePromoted: number; // 1 = force promoted
  StageTypeCode: string; // StageType.PRECLOSE | StageType.CLOSE
  StageChangeReason: string;
};

// Single-record flag update for SavePolicyTransaction (clear Hold / FP / Edited).
export type PolicyTransactionFlagUpdate = {
  IdentityKey: number;
  IsOnHold?: boolean;
  IsForcePromoted?: boolean;
  IsEdited?: boolean;
};

export type PolicyTransactionOverrideFinancials = {
  IdentityKey: number;
  IsOverride: boolean;
  OverrideBy?: string;
  OverrideDate?: string;
  BookedTypeCode: string;
  NewRemitSplitPct: number;
  ReissueLiabilityAmount: number;
  SILiabilityAmount: number;
  AdditionalLiabilityAmount: number;

  CalculatedFee?: number;
  CalculatedPremiumTax?: number;
  CalculatedRemittance?: number;
  CalculatedRiskRate?: number;

  ActualFee?: number;
  ActualRemittance?: number;
  ActualRiskRate?: number;
  ActualPremiumTax?: number;
};

// Single-record agency/location update for SavePolicyTransaction (the
// Transfer action). Mirrors the Company/Location columns on PolicyBaseEntity.
export type PolicyTransactionTransferUpdate = {
  PolicyTransactionID: number;
  CompanyID?: string;
  CompanyName?: string;
  CompanyLegacyID?: string;
  LocationUniqueID?: string;
  LocationDisplayName?: string;
  LocationReportingID?: string;
  LocationAgencyCompanyID?: string;
};

export const savePolicyTransaction = async (
  entity: PolicyTransactionFlagUpdate | PolicyTransactionTransferUpdate | PolicyTransactionOverrideFinancials
): Promise<boolean> => {
  try {
    await axiosSecuredInstance.post(
      "/PolicyTransaction/SavePolicyTransaction",
      entity
    );
    return true;
  } catch (error) {
    console.error("savePolicyTransaction failed", error);
    return false;
  }
};

export const releasePolicyTransactionLock = async (payload: {
  PolicyTransactionID: number;
}): Promise<boolean> => {
  try {
    await axiosSecuredInstance.post(
      "/PolicyTransaction/ReleasePolicyTransactionLock",
      payload
    );
    return true;
  } catch (error) {
    console.error("releasePolicyTransactionLock failed", error);
    return false;
  }
};

// Mirrors Stewart.Shared.Entities.PolicyData.PolicyTransactionLockResult.
export type PolicyTransactionLockResultDto = {
  PolicyTransactionID: number;
  LockID: number | null;
  LockAcquired: boolean;
  ExistingLockedByUserName?: string | null;
  ExistingLockContext?: string | null;
  ExistingExpiresDate?: string | null;
};

// LockAcquired is the authoritative answer to "may I edit this record?" — a
// conflict comes back as 200 with LockAcquired: false plus the Existing* fields
// describing the holder, never as an error status, so the banner can name them.
// Re-acquiring a lock you already hold succeeds and extends it.
export const acquirePolicyTransactionLock = async (payload: {
  PolicyTransactionID: number;
  LockContext: "IN_EDIT";
}): Promise<PolicyTransactionLockResultDto | null> => {
  try {
    const { data } = await axiosSecuredInstance.post<PolicyTransactionLockResultDto>(
      "/PolicyTransaction/AcquirePolicyTransactionLock",
      payload
    );
    return data ?? null;
  } catch (error) {
    console.error("acquirePolicyTransactionLock failed", error);
    return null;
  }
};

export const insertPolicyTransactionStage = async (
  entity: PolicyTransactionStageChange
): Promise<boolean> => {
  try {
    await axiosSecuredInstance.post(
      "/PolicyTransaction/SaveStageChange",
      entity
    );
    return true;
  } catch (error) {
    console.error("insertPolicyTransactionStage failed", error);
    return false;
  }
};

export const getPolicyTransactionList = async (
  criteria: PolicyTransactionCriteriaDto
): Promise<PolicyTransactionDto[]> => {
  try {
    const { data } = await axiosSecuredInstance.post<PolicyTransactionDto[]>(
      "/PolicyTransaction/GetPolicyTransactionListByCriteria",
      criteria
    );
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("getPolicyTransactionList failed", error);
    return [];
  }
};

// Fetch a single transaction by id. Used by the Product Details page so it can
// re-hydrate on browser refresh / deep-link (nav state is lost on refresh).
export const getPolicyTransactionById = async (
  policyTransactionID: number
): Promise<PolicyTransactionDto | null> => {
  try {
    const { data } = await axiosSecuredInstance.get<PolicyTransactionDto>(
      "/PolicyTransaction/GetPolicyTransactionByID",
      { params: { policyTransactionID } }
    );
    return data ?? null;
  } catch (error) {
    console.error("getPolicyTransactionById failed", error);
    return null;
  }
};

// Fetch every line item (product/transaction) that belongs to the same file,
// keyed by file number + agent. Backs the File View section on Product Details.
// Backend: BUS_GetFileByNumberAgent via TransactionController.GetFileByNumberAgent.
export const getFileByNumberAgent = async (
  fileNumber: string,
  agentID: string
): Promise<PolicyTransactionDto[]> => {
  try {
    const { data } = await axiosSecuredInstance.get<PolicyTransactionDto[]>(
      "/PolicyTransaction/GetFileByNumberAgent",
      { params: { fileNumber, agentID } }
    );
    return Array.isArray(data) ? data : [];
  } catch {
    showToast({ type: "error", message: "Failed to load file view." });
    return [];
  }
};

// Mirrors Stewart.Shared.Entities.PolicyData.PolicyGlobalSearchCriteria.
// AgentID / ReportingID are exact matches; FileNumber / PolicyNumber are
// partial (LIKE '%value%'). Every supplied field must match.
export type PolicyGlobalSearchCriteriaDto = {
  AgentID?: string;
  ReportingID?: string;
  FileNumber?: string;
  PolicyNumber?: string;
  AgencyType?: string;
  StateAbbrList?: string;
};

// Mirrors Stewart.Shared.Entities.PolicyData.PolicyGlobalSearchResult.
export type PolicyGlobalSearchResultDto = {
  QualityReviewCount: number;
  PreCloseCount: number;
  CloseCount: number;
  HasHistoryRecords: boolean;
};

// Policy Management Global Search: which queues hold matching records.
// Backend: BUS_PolicyGlobalSearch via TransactionController.GetPolicyGlobalSearch.
// Returns null on failure so the caller can render its own error state.
export const getPolicyGlobalSearch = async (
  criteria: PolicyGlobalSearchCriteriaDto
): Promise<PolicyGlobalSearchResultDto | null> => {
  try {
    const { data } = await axiosSecuredInstance.post<PolicyGlobalSearchResultDto>(
      "/PolicyTransaction/GetPolicyGlobalSearch",
      criteria
    );
    return data ?? null;
  } catch (error) {
    console.error("getPolicyGlobalSearch failed", error);
    return null;
  }
};

// Close a single policy transaction (Close Queue action).
export const closePolicyTransaction = async (
  policyTransactionID: number
): Promise<boolean> => {
  try {
    await axiosSecuredInstance.post(
      "/PolicyTransaction/ClosePolicyTransaction",
      null,
      { params: { policyTransactionID } }
    );
    return true;
  } catch (error) {
    console.error("closePolicyTransaction failed", error);
    return false;
  }
};

// Mirrors Stewart.Shared.Entities.PolicyData.PolicyTransactionCount — refreshed
// stage counts returned by BUS_CloseBatch after a Close Queue batch close.
export type PolicyTransactionCountDto = {
  QualityReviewCount: number;
  PreCloseCount: number;
  CloseCount: number;
  HistoryCount: number;
  HoldCount: number;
  TotalCount: number;
};

// Close every record matching the given criteria in one server-side batch
// (BUS_CloseBatch). Pass PolicyTransactionIDList for "Close Selected", or omit
// it (with the State/Received Through filters) for "Close All".
export const closePolicyTransactionBatch = async (
  criteria: Partial<PolicyTransactionCriteriaDto>
): Promise<PolicyTransactionCountDto | null> => {
  try {
    const { data } = await axiosSecuredInstance.post<PolicyTransactionCountDto>(
      "/PolicyTransaction/ClosePolicyTransactionBatch",
      criteria
    );
    return data ?? null;
  } catch (error) {
    console.error("closePolicyTransactionBatch failed", error);
    return null;
  }
};

// Mirrors Stewart.Shared.Entities.PolicyData.PolicyClosePreviewReport — one
// row of the by-state breakdown returned by GetClosePreviewReport.
export type PolicyClosePreviewReportDto = {
  StateCode?: string;
  LiabilityAmount?: number;
  ActualFee?: number;
  ActualRemittance?: number;
  ProductCount?: number;
  SIPolicyCount?: number;
};

// Mirrors Stewart.Shared.Entities.PolicyData.PolicyClosePreviewSummary —
// server-computed totals for the Close Queue "Confirm Close" preview.
export type PolicyClosePreviewSummaryDto = {
  TotalProducts?: number;
  TotalSimultaneous?: number;
  TotalLiabilityAmount?: number;
  TotalActualFee?: number;
  TotalActualRemittance?: number;
  TotalStates?: number;
  StateSummary: PolicyClosePreviewReportDto[];
};

// Fresh, server-side batch summary for the Confirm Close preview (BUS-backed
// via GetPolicyTransactionListByCriteria under the hood). Pass the same
// PolicyTransactionIDList that will be used to actually close the batch so
// the preview matches what gets closed.
export const getClosePreviewReport = async (
  criteria: Partial<PolicyTransactionCriteriaDto>
): Promise<PolicyClosePreviewSummaryDto | null> => {
  try {
    const { data } = await axiosSecuredInstance.post<PolicyClosePreviewSummaryDto>(
      "/PolicyTransaction/GetClosePreviewReport",
      criteria
    );
    return data ?? null;
  } catch (error) {
    console.error("getClosePreviewReport failed", error);
    return null;
  }
};

// Downloads the by-state CSV breakdown backing the Confirm Close preview
// (same criteria used for getClosePreviewReport/closePolicyTransactionBatch).
export const exportClosePreviewReport = async (
  criteria: Partial<PolicyTransactionCriteriaDto>
): Promise<boolean> => {
  try {
    const response = await axiosSecuredInstance.post(
      "/PolicyTransaction/ExportClosePreviewReport",
      criteria,
      { responseType: "blob" }
    );
    const filename = `Close Preview Report ${dateTimeAsFilename()}.${DownloadFileType.Xlsx}`;
    const blobUrl = URL.createObjectURL(
      new Blob([response.data], { type: getBase64DataType(DownloadFileType.Xlsx) })
    );
    downloadByBase64(blobUrl, filename);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    return true;
  } catch (error) {
    console.error("exportClosePreviewReport failed", error);
    return false;
  }
};

export const bulkHoldPolicyTransaction = async (
  bulkHoldPolicyPayload: BulkHoldPolicyTransactionDto
): Promise<boolean> => {
  try {
    await axiosSecuredInstance.post(
      "/PolicyTransaction/UpdateList",
      bulkHoldPolicyPayload
    );
    return true;
  } catch (error) {
    console.error("bulkHoldPolicyTransaction failed", error);
    return false;
  }
}

export const setPolicyTransactionDeletedList = async (
  payload: SetPolicyTransactionDeletedListDto
): Promise<SetPolicyTransactionDeletedListResult> => {
  try {
    await axiosSecuredInstance.post(
      "/PolicyTransaction/SetPolicyTransactionDeletedList",
      payload
    );
    return { success: true };
  } catch (error: any) {
    console.error("setPolicyTransactionDeletedList failed", error);
    return {
      success: false,
      errorMessage: error?.response?.data?.ErrorMessage,
    };
  }
}

export const bulkEditPolicyTransaction = async (
  bulkEditPolicyPayload: any
): Promise<boolean> => {
  try {
    await axiosSecuredInstance.post(
      "/PolicyTransaction/UpdatePolicyTransactionList",
        bulkEditPolicyPayload
    );
    return true;
  } catch (error) {
    console.error("bulkEditPolicyTransaction failed", error);
    return false;
  }
}

// Restore one or more deleted records back to their original queue.
export type RestorePolicyTransactionResultDto = {
  RestoredCount: number;
  FailedCount: number;
  FailedPolicyTransactionIDList?: string[];
};

export const restorePolicyTransactionBatch = async (
  criteria: Partial<PolicyTransactionCriteriaDto>
): Promise<RestorePolicyTransactionResultDto | null> => {
  try {
    const { data } = await axiosSecuredInstance.post<RestorePolicyTransactionResultDto>(
      "/PolicyTransaction/RestorePolicyTransactionBatch",
      criteria
    );
    return data ?? null;
  } catch (error) {
    console.error("restorePolicyTransactionBatch failed", error);
    return null;
  }
};
